<script lang="ts">
  /**
   * Access — guest passes, sessions, and "see it as they see it".
   * Items 8, 9 and 11.
   *
   * The three panels answer three different questions that used to require the
   * Firebase console and a certain amount of nerve: who is currently allowed in
   * and until when, which devices are signed in, and what a `display` or a
   * `guest` would actually see.
   */
  import { Icon, dateZA } from "@steyncd/steyn-design";
  import { api, ApiError } from "../lib/api";
  import { stepUp } from "../lib/stepup.svelte";

  type Guest = {
    uid: string;
    email: string | null;
    displayName: string | null;
    modules: string[];
    expiresAt: string | null;
    msRemaining: number | null;
    expired: boolean;
    revokedAt: string | null;
    lastSeenAt: string | null;
    /** `module:waypoint` when a trip armed it, an owner's email when a person did. */
    issuedBy: string | null;
    tripId: string | null;
  };
  type Preset = { id: string; label: string; modules: string[]; expiresAt: string };
  type UserRow = { uid: string; displayName?: string; email?: string; role: string };
  type Session = { deviceHash: string; lastSeen: string | null; ua: string; network: string | null; country: string | null };
  type PortalPreview = { id: string; name: string; minRole: string; status: string };

  type Panel<T> = { data: T | null; error: string | null; loading: boolean };

  const guests = $state<Panel<{ guests: Guest[]; presets: Preset[] }>>({ data: null, error: null, loading: true });
  const people = $state<Panel<{ users: UserRow[] }>>({ data: null, error: null, loading: true });
  const preview = $state<Panel<{ portals: PortalPreview[]; preview?: { asRole: string; asModules: string[] } }>>({
    data: null, error: null, loading: false,
  });

  let sessionsFor = $state<string | null>(null);
  const sessions = $state<Panel<{ sessions: Session[] }>>({ data: null, error: null, loading: false });

  let busy = $state<string | null>(null);
  let toast = $state<{ kind: "ok" | "warn"; text: string } | null>(null);

  /** Issue form. A uid, not an email — see the note on POST /guests. */
  let issue = $state<{ uid: string; preset: string; expiresAt: string }>({ uid: "", preset: "housesitter-weekend", expiresAt: "" });
  let asRole = $state<"owner" | "partner" | "display" | "guest">("display");
  let asModules = $state("ha");
  /** Ticks once a second so the countdown is a countdown, not a stale number. */
  let now = $state(Date.now());

  async function fill<T>(p: Panel<T>, path: string) {
    p.loading = true;
    try {
      p.data = await api.get<T>(path);
      p.error = null;
    } catch (e) {
      p.error = e instanceof ApiError ? e.message : "Unavailable";
    } finally {
      p.loading = false;
    }
  }

  $effect(() => {
    void fill(guests, "/guests");
    void fill(people, "/users");
  });

  $effect(() => {
    const t = setInterval(() => (now = Date.now()), 1000);
    return () => clearInterval(t);
  });

  async function issuePass() {
    if (!issue.uid) return;
    busy = "issue";
    try {
      const ok = await stepUp.guard(
        () =>
          api.post("/guests", {
            uid: issue.uid,
            preset: issue.preset,
            ...(issue.expiresAt ? { expiresAt: `${issue.expiresAt}T18:00:00+02:00` } : {}),
          }),
        "Issuing a guest pass",
      );
      if (ok) {
        toast = { kind: "ok", text: "Pass issued. Their next sign-in carries it." };
        issue = { uid: "", preset: issue.preset, expiresAt: "" };
        await fill(guests, "/guests");
      } else {
        toast = { kind: "warn", text: "Cancelled — no pass issued." };
      }
    } catch (e) {
      toast = { kind: "warn", text: e instanceof ApiError ? e.message : "Could not issue the pass" };
    } finally {
      busy = null;
    }
  }

  async function revoke(g: Guest) {
    busy = g.uid;
    try {
      const ok = await stepUp.guard(() => api.del(`/guests/${g.uid}`), `Revoking ${g.email ?? g.uid}'s pass`);
      toast = ok
        ? { kind: "ok", text: `${g.email ?? g.uid} is out, immediately.` }
        : { kind: "warn", text: "Cancelled — the pass is still live." };
      if (ok) await fill(guests, "/guests");
    } catch (e) {
      toast = { kind: "warn", text: e instanceof ApiError ? e.message : "Could not revoke" };
    } finally {
      busy = null;
    }
  }

  async function revokeAll() {
    busy = "revoke-all";
    try {
      const ok = await stepUp.guard(
        () => api.post<{ count: number }>("/sessions/revoke-all"),
        "Signing every device out, including the lounge TV",
      );
      toast = ok ? { kind: "ok", text: "Everyone signed out everywhere." } : { kind: "warn", text: "Cancelled." };
    } catch (e) {
      toast = { kind: "warn", text: e instanceof ApiError ? e.message : "Could not revoke sessions" };
    } finally {
      busy = null;
    }
  }

  async function showSessions(uid: string) {
    sessionsFor = uid;
    await fill(sessions, `/users/${uid}/sessions`);
  }

  async function runPreview() {
    await fill(preview, `/portals?asRole=${asRole}&asModules=${encodeURIComponent(asModules)}`);
  }

  function countdown(ms: number | null): string {
    if (ms === null) return "no expiry";
    if (ms <= 0) return "expired";
    const d = Math.floor(ms / 86_400_000);
    const h = Math.floor((ms % 86_400_000) / 3_600_000);
    const m = Math.floor((ms % 3_600_000) / 60_000);
    if (d > 0) return `${d}d ${h}h left`;
    if (h > 0) return `${h}h ${m}m left`;
    return `${m}m left`;
  }
</script>

<div class="wrap">
  {#if toast}
    <div class="attn" class:attn--warn={toast.kind === "warn"} role="status">
      <span class="attn__badge" aria-hidden="true"><Icon name={toast.kind === "warn" ? "alert" : "check"} size={12} /></span>
      <span class="attn__text">{toast.text}</span>
    </div>
  {/if}

  <!-- ── Guest passes ──────────────────────────────────────────────────── -->
  <section class="card">
    <div class="sec"><span class="kicker">Guest passes</span><span class="sub">role, modules and expiry set together</span></div>

    {#if guests.loading}
      <p class="sub pad">Loading…</p>
    {:else if guests.error}
      <p class="sub pad"><Icon name="alert" size={12} /> {guests.error}</p>
    {:else if guests.data}
      <div class="form">
        <label>
          <span class="lb">Preset</span>
          <select bind:value={issue.preset}>
            {#each guests.data.presets as p (p.id)}
              <option value={p.id}>{p.label}</option>
            {/each}
          </select>
        </label>
        <label>
          <span class="lb">Account</span>
          <select bind:value={issue.uid}>
            <option value="">Choose a signed-in account…</option>
            {#each people.data?.users ?? [] as u (u.uid)}
              <option value={u.uid}>{u.displayName ?? u.email ?? u.uid}</option>
            {/each}
          </select>
        </label>
        <label>
          <span class="lb">Ends (optional)</span>
          <input type="date" bind:value={issue.expiresAt} />
        </label>
        <button class="btn-primary" disabled={!issue.uid || busy === "issue"} onclick={issuePass}>
          {busy === "issue" ? "Issuing…" : "Issue pass"}
        </button>
      </div>
      <p class="sub foot">
        <Icon name="alert" size={12} />
        A pass can only be given to an account that has signed in at least once — claims attach to a uid, and a
        uid does not exist before then. The preset's modules are a ceiling, never widened.
        {#if guests.data.presets.length > 0}
          “{guests.data.presets[0].label}” currently ends {dateZA(guests.data.presets[0].expiresAt)}.
        {/if}
      </p>

      {#if guests.data.guests.length === 0}
        <p class="sub pad"><Icon name="pause" size={12} /> No guest accounts.</p>
      {:else}
        <div class="scroll">
          <table>
            <thead><tr><th>Guest</th><th>Modules</th><th>Countdown</th><th>Last seen</th><th></th></tr></thead>
            <tbody>
              {#each guests.data.guests as g (g.uid)}
                <tr class:off={g.expired}>
                  <td>
                    <div class="nm">{g.displayName ?? g.uid}</div>
                    <div class="sub">{g.email ?? ""}</div>
                    <!-- Words, not a badge: "a module let this person in" is a
                         sentence, and a coloured dot would be a puzzle. -->
                    {#if g.issuedBy}
                      <div class="sub">
                        issued by {g.issuedBy}{g.tripId ? ` · trip ${g.tripId}` : ""}
                      </div>
                    {/if}
                  </td>
                  <td class="sub">{g.modules.join(", ") || "—"}</td>
                  <td>
                    {#if g.expired}
                      <span class="status status--idle"><Icon name="pause" size={12} /> expired</span>
                    {:else}
                      <span class="status status--ok">
                        <Icon name="clock" size={12} />
                        {countdown(g.expiresAt ? Date.parse(g.expiresAt) - now : null)}
                      </span>
                    {/if}
                  </td>
                  <td class="sub num">{g.lastSeenAt ? dateZA(g.lastSeenAt) : "never"}</td>
                  <td class="acts">
                    <button class="lnk" disabled={busy === g.uid || g.expired} onclick={() => revoke(g)}>Revoke now</button>
                  </td>
                </tr>
              {/each}
            </tbody>
          </table>
        </div>
      {/if}
    {/if}
  </section>

  <!-- ── Sessions ──────────────────────────────────────────────────────── -->
  <section class="card">
    <div class="sec">
      <span class="kicker">Devices</span>
      <button class="btn-primary" disabled={busy === "revoke-all"} onclick={revokeAll}>
        {busy === "revoke-all" ? "Revoking…" : "Revoke every session"}
      </button>
    </div>
    <div class="chips">
      {#each people.data?.users ?? [] as u (u.uid)}
        <button class="chip" class:chip--on={sessionsFor === u.uid} onclick={() => showSessions(u.uid)}>
          {u.displayName ?? u.email ?? u.uid}
        </button>
      {/each}
    </div>
    {#if sessionsFor}
      {#if sessions.loading}
        <p class="sub pad">Loading…</p>
      {:else if sessions.error}
        <p class="sub pad"><Icon name="alert" size={12} /> {sessions.error}</p>
      {:else if (sessions.data?.sessions ?? []).length === 0}
        <p class="sub pad"><Icon name="pause" size={12} /> No devices recorded yet — one appears on their next visit.</p>
      {:else}
        <div class="scroll">
          <table>
            <thead><tr><th>Device</th><th>Network</th><th>Last seen</th></tr></thead>
            <tbody>
              {#each sessions.data!.sessions as s (s.deviceHash)}
                <tr>
                  <td class="nm">{s.ua}</td>
                  <td class="sub num">{s.network ?? "—"}{s.country ? ` · ${s.country}` : ""}</td>
                  <td class="sub num">{s.lastSeen ? dateZA(s.lastSeen) : "—"}</td>
                </tr>
              {/each}
            </tbody>
          </table>
        </div>
      {/if}
    {:else}
      <p class="sub pad">Pick a person to see their devices. The lounge TV lives here too.</p>
    {/if}
    <p class="sub foot">
      Coarse by design: a device label and a /24 network, nothing finer. This is a household, not a SIEM.
    </p>
  </section>

  <!-- ── Role preview ──────────────────────────────────────────────────── -->
  <section class="card">
    <div class="sec"><span class="kicker">See it as they see it</span><span class="sub">read-only preview</span></div>
    <div class="form">
      <label>
        <span class="lb">As role</span>
        <select bind:value={asRole}>
          <option value="partner">partner</option>
          <option value="display">display</option>
          <option value="guest">guest</option>
          <option value="owner">owner</option>
        </select>
      </label>
      <label>
        <span class="lb">With modules</span>
        <input bind:value={asModules} placeholder="ha,screening" />
      </label>
      <button class="btn-primary" onclick={runPreview} disabled={preview.loading}>
        {preview.loading ? "Rendering…" : "Preview"}
      </button>
    </div>

    {#if preview.error}
      <p class="sub pad"><Icon name="alert" size={12} /> {preview.error}</p>
    {:else if preview.data}
      <!-- Labelled unmistakably, so a screenshot of this is never mistaken for
           the real thing. It never mints a token. -->
      <div class="attn attn--info previewbar">
        <span class="attn__badge attn__badge--info" aria-hidden="true"><Icon name="user" size={12} /></span>
        <span class="attn__text">
          Preview only · <strong>{preview.data.preview?.asRole}</strong> with
          {preview.data.preview?.asModules.join(", ") || "no modules"} would see {preview.data.portals.length} portal{preview.data.portals.length === 1 ? "" : "s"}
        </span>
      </div>
      <div class="chips">
        {#each preview.data.portals as p (p.id)}
          <span class="chip">{p.name} <span class="sub">· {p.minRole}</span></span>
        {/each}
      </div>
      {#if preview.data.portals.length === 0}
        <p class="sub pad">Nothing at all — which is the correct answer for a role with no grants.</p>
      {/if}
    {/if}
  </section>
</div>

<style>
  .wrap { display: flex; flex-direction: column; gap: 14px; }
  .card { padding: 13px 15px 15px; }
  .sec { display: flex; align-items: baseline; justify-content: space-between; gap: 10px; padding-bottom: 9px; }
  .pad { padding: 6px 0 2px; display: flex; align-items: center; gap: 6px; }
  .foot { margin: 9px 0 0; display: flex; align-items: flex-start; gap: 6px; }
  .scroll { overflow-x: auto; }
  table { width: 100%; border-collapse: collapse; font-size: 0.7812rem; }
  th { text-align: left; font-size: 0.72rem; font-weight: 700; color: var(--mut); padding: 6px 8px;
       border-bottom: 1px solid var(--line); white-space: nowrap; }
  td { padding: 9px 8px; border-bottom: 1px solid var(--line); vertical-align: middle; }
  tbody tr:last-child td { border-bottom: none; }
  tr.off { opacity: 0.55; }
  .nm { font-weight: 700; color: var(--tx); }
  .acts { display: flex; gap: 8px; justify-content: flex-end; }
  .lnk { font-size: 0.72rem; font-weight: 700; color: var(--mut); }
  .lnk:hover { color: var(--acc); }
  button:disabled { opacity: 0.45; cursor: default; }

  .form { display: flex; flex-wrap: wrap; gap: 10px; align-items: flex-end; padding: 2px 0 8px; }
  .form label { display: flex; flex-direction: column; gap: 4px; }
  select, input { background: var(--fill); color: var(--tx); border: 1px solid var(--line);
                  border-radius: var(--r-control); padding: 6px 8px; font-size: 0.75rem; }

  .chips { display: flex; flex-wrap: wrap; gap: 6px; padding: 2px 0 8px; }
  .chip { padding: 3px 9px; border-radius: var(--r-pill); box-shadow: inset 0 0 0 1px var(--line);
          font-size: 0.72rem; font-weight: 700; color: var(--tx2); }
  .chip--on { color: var(--tx); background: var(--wash); box-shadow: inset 0 0 0 1px var(--acc); }
  .previewbar { margin: 6px 0 10px; }
</style>
